import React, { useState, useEffect } from 'react'
import {
  Card,
  Descriptions,
  Button,
  Space,
  Tag,
  Timeline,
  Alert,
  Modal,
  Input,
  message,
  Spin,
  Typography,
  Row,
  Col,
  Badge,
  Tabs,
  Result,
  Empty,
  notification
} from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faDownload,
  faTimes,
  faCheckCircle,
  faHourglassHalf,
  faClipboard,
  faFlask,
  faUser,
  faCalendar,
  faNoteSticky,
  faHistory
} from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import { ORDER_STATUS, PRIORITY_LEVEL_VALUES, PRIORITY_LEVELS } from '../utils/constants'
import { useLabOrders } from '../hooks/useLabOrders'
import { validateResultSchema, formatLabDate } from '../utils/resultUtils'
import { DocumentViewer } from '../../reusable-components/DocumentViewer'

const { Title, Text, Paragraph } = Typography
const { TabPane } = Tabs
const { TextArea } = Input

const OrderDetail = ({ order, onBack }) => {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [activeTab, setActiveTab] = useState('overview')
  const [selectedPdfIndex, setSelectedPdfIndex] = useState(0)
  const [testResults, setTestResults] = useState(null)

  const { cancelOrder, resultsByOrderNumber, handleDownloadPDF } = useLabOrders()

  useEffect(() => {
    const getResults = async () => {
      try {
        setLoading(true)
        const tmpResult = await resultsByOrderNumber(order.orderNumber, true)

        // Validate the result schema
        const validation = validateResultSchema(tmpResult)
        if (!validation.isValid) {
          console.warn('Result schema validation failed:', validation.errors)
        }

        setResult(tmpResult)

        // Base64 to XML
        const xmlString = atob(tmpResult.pathologyReports.xml)
        setTestResults(parseBloodTestResults(xmlString))
      } catch (error) {
        console.error('Failed to fetch results:', error)
        notification.error({
          message: 'Failed to Load Results',
          description: 'Unable to retrieve lab results. Please try again later.'
        })
      } finally {
        setLoading(false)
      }
    }

    if (order?.status === ORDER_STATUS.COMPLETE) {
      getResults()
    }
  }, [order, resultsByOrderNumber])

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      message.error('Please provide a reason for cancellation')
      return
    }

    setLoading(true)
    try {
      await cancelOrder(order.orderNumber, order.orderData.doctor)
      setShowCancelModal(false)
      onBack()
    } catch (error) {
      message.error('Failed to cancel order')
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      [ORDER_STATUS.ORDERED]: { status: 'processing', text: 'Ordered', icon: faHourglassHalf },
      [ORDER_STATUS.COMPLETE]: { status: 'success', text: 'Complete', icon: faCheckCircle },
      [ORDER_STATUS.EXPIRED]: { status: 'error', text: 'Expired', icon: faTimes },
      [ORDER_STATUS.PROCESSING]: { status: 'processing', text: 'Processing', icon: faHourglassHalf }
    }

    const config = statusConfig[status] || statusConfig[ORDER_STATUS.PENDING]

    return (
      <Badge
        status={config.status}
        text={
          <Space>
            <FontAwesomeIcon icon={config.icon} />
            {config.text}
          </Space>
        }
      />
    )
  }

  const getPriorityText = (priority) => {
    switch (priority) {
      case PRIORITY_LEVEL_VALUES.STAT:
        return PRIORITY_LEVELS.STAT
      case PRIORITY_LEVEL_VALUES.ROUTINE:
        return PRIORITY_LEVELS.ROUTINE
      default:
        return 'Unknown'
    }
  }

  const renderTimeline = () => {
    const events = [
      {
        time: order.createdAt,
        title: 'Created',
        description: `Created by ${order.medicalProfRecord.firstName} ${order.medicalProfRecord.lastName}`,
        color: 'blue'
      }
    ]

    order.timeline.forEach((event) => {
      let description = ''

      switch (event.status) {
        case 'Ordered':
          description = `Ordered by ${order.medicalProfRecord.firstName} ${order.medicalProfRecord.lastName}`
          break
        case 'Complete':
          description = 'Lab results completed and available for review'
          break
        case 'Expired':
          description = 'Order expired'
          break
        case 'Processing':
          description = 'Lab analysis in progress'
          break
        default:
          description = `Unknown event: ${event.status}`
          break
      }

      events.push({
        time: event.timestamp,
        title: event.status,
        description: description,
        color: event.status === 'COMPLETE' ? 'green' : event.status === 'EXPIRED' ? 'red' : 'blue'
      })
    })

    return (
      <Timeline mode='left'>
        {events.map((event, index) => (
          <Timeline.Item key={index} color={event.color} label={moment(event.time).format('YYYY-MM-DD HH:mm')}>
            <Text strong>{event.title}</Text>
            <br />
            <Text type='secondary'>{event.description}</Text>
          </Timeline.Item>
        ))}
      </Timeline>
    )
  }

  /**
   * Parses a PathologyReports XML string and extracts each test order with its result.
   * @param {string} xmlString - The XML document as a string.
   * @returns {Array<Object>} - List of test objects: { code, name, value, units, range, flag, comment }.
   */
  const parseBloodTestResults = (xmlString) => {
    // Parse XML string into a DOM
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlString, 'application/xml')

    // Collect all <Test> elements
    const tests = Array.from(xmlDoc.querySelectorAll('Specimen Procedure Test'))

    // Map to an array of result objects
    return tests.map((testEl) => {
      const code = testEl.getAttribute('code')
      const name = testEl.getAttribute('name')
      const resultEl = testEl.querySelector('Result')
      const value = resultEl?.getAttribute('value') ?? null
      const units = resultEl?.getAttribute('units') ?? null
      const range = resultEl?.getAttribute('range') ?? null
      const flag = resultEl?.getAttribute('flag') ?? null
      // Grab comment text, if any
      const commentEl = resultEl?.querySelector('Comment Localization[language="en"] Text')
      const comment = commentEl ? commentEl.textContent.trim().replace(/\s+/g, ' ') : null

      return { code, name, value, units, range, flag, comment }
    })
  }

  // Render the Test Results tab content
  const renderTestResults = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size='large' />
          <div style={{ marginTop: 16 }}>
            <Text>Loading test results...</Text>
          </div>
        </div>
      )
    }

    if (!result) {
      return <Empty description='No results available' image={Empty.PRESENTED_IMAGE_SIMPLE} />
    }

    if (result.error && result.error !== '') {
      return (
        <Alert
          message='Error Loading Results'
          description={result.error}
          type='error'
          showIcon
          style={{ margin: '20px 0' }}
        />
      )
    }

    // PDF rendering logic
    const pdfs = result.pathologyReportPDFs || []
    const hasPDFs = pdfs.length > 0

    return (
      <Space direction='vertical' size='large' style={{ width: '100%' }}>
        {/* Results Summary Card */}
        <Card type='inner' title='Test Results Summary'>
          <Descriptions column={2}>
            <Descriptions.Item label='Batch Number'>{result.batchNo || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label='Latest Result Date'>{formatLabDate(result.latestResult)}</Descriptions.Item>
            <Descriptions.Item label='All Specimens Complete'>
              <Tag color={result.allSpecimensComplete ? 'green' : 'orange'}>
                {result.allSpecimensComplete ? 'Complete' : 'Pending'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label='More Results Available'>
              <Tag color={result.hasMoreResults ? 'blue' : 'default'}>{result.hasMoreResults ? 'Yes' : 'No'}</Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {testResults && testResults.length > 0 && (
          <Card type='inner' title='Test Results' size='small'>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e8e8e8' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Test</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Result</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Reference Range</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {testResults.map((test, index) => (
                    <tr
                      key={test.code}
                      style={{
                        borderBottom: index < testResults.length - 1 ? '1px solid #f0f0f0' : 'none',
                        ':hover': { backgroundColor: '#f9f9f9' }
                      }}
                    >
                      <td style={{ padding: '8px 12px', verticalAlign: 'top' }}>
                        <div>
                          <Text strong style={{ display: 'block', marginBottom: '2px' }}>
                            {test.name}
                          </Text>
                          {test.code && (
                            <Text type='secondary' style={{ fontSize: '12px' }}>
                              {test.code}
                            </Text>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '8px 12px', verticalAlign: 'top' }}>
                        <div>
                          <Space>
                            <Text strong style={{ fontSize: '15px' }}>
                              {test.value || 'N/A'}
                            </Text>
                            {test.units && (
                              <Text type='secondary' style={{ fontSize: '13px' }}>
                                {test.units}
                              </Text>
                            )}
                          </Space>
                          {test.comment && (
                            <div style={{ marginTop: '4px' }}>
                              <Text type='secondary' style={{ fontSize: '12px', fontStyle: 'italic' }}>
                                {test.comment}
                              </Text>
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '8px 12px', verticalAlign: 'top' }}>
                        <Text type='secondary' style={{ fontSize: '13px' }}>
                          {test.range || '-'}
                        </Text>
                      </td>
                      <td style={{ padding: '8px 12px', verticalAlign: 'top' }}>
                        {test.flag ? (
                          <Tag
                            size='small'
                            color={
                              test.flag.toLowerCase() === 'high'
                                ? 'red'
                                : test.flag.toLowerCase() === 'low'
                                ? 'orange'
                                : test.flag.toLowerCase() === 'critical'
                                ? 'purple'
                                : 'default'
                            }
                          >
                            {test.flag}
                          </Tag>
                        ) : (
                          <Text type='secondary'>-</Text>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* PDF Reports Inline Viewer */}
        {hasPDFs && (
          <Card type='inner' title={`PDF Report${pdfs.length > 1 ? 's' : ''}`}>
            {pdfs.length > 1 && (
              <Space style={{ marginBottom: 12 }}>
                {pdfs.map((pdf, idx) => (
                  <Button
                    key={pdf.messageId}
                    type={selectedPdfIndex === idx ? 'primary' : 'default'}
                    onClick={() => setSelectedPdfIndex(idx)}
                  >
                    Report #{pdf.messageId}
                  </Button>
                ))}
              </Space>
            )}
            <div style={{ border: '1px solid #eee', borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
              <DocumentViewer base64={pdfs[selectedPdfIndex].pdf} />
            </div>
            <Button
              type='primary'
              icon={<FontAwesomeIcon icon={faDownload} />}
              onClick={() =>
                handleDownloadPDF(pdfs[selectedPdfIndex].pdf, order.orderNumber, pdfs[selectedPdfIndex].messageId)
              }
            >
              Download PDF
            </Button>
          </Card>
        )}

        {/* Status Alerts */}
        {!result.allSpecimensComplete && (
          <Alert
            message='Specimen Processing Incomplete'
            description='Some specimens are still being processed. Results may be updated as processing completes.'
            type='warning'
            showIcon
          />
        )}

        {result.hasMoreResults && (
          <Alert
            message='Additional Results Available'
            description='There may be additional results available. Please refresh or check back later.'
            type='info'
            showIcon
          />
        )}
      </Space>
    )
  }

  if (!order) {
    return (
      <Result
        status='404'
        title='Order Not Found'
        subTitle='The requested lab order could not be found.'
        extra={<Button onClick={onBack}>Back to List</Button>}
      />
    )
  }

  return (
    <Card
      title={
        <Space align='center' size='large'>
          <Button icon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={onBack}>
            Back
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            Lab Order #{order.orderNumber}
          </Title>
          {getStatusBadge(order.status)}
        </Space>
      }
      extra={
        <Space>
          {order.status === ORDER_STATUS.COMPLETE && result?.pathologyReportPDFs?.length > 0 && (
            <Button
              type='primary'
              icon={<FontAwesomeIcon icon={faDownload} />}
              onClick={() =>
                handleDownloadPDF(
                  result.pathologyReportPDFs[0].pdf,
                  order.orderNumber,
                  result.pathologyReportPDFs[0].messageId
                )
              }
            >
              Download Results
            </Button>
          )}
          {order.status === ORDER_STATUS.ORDERED && (
            <Button danger icon={<FontAwesomeIcon icon={faTimes} />} onClick={() => setShowCancelModal(true)}>
              Cancel Order
            </Button>
          )}
        </Space>
      }
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <Space>
              <FontAwesomeIcon icon={faClipboard} />
              Overview
            </Space>
          }
          key='overview'
        >
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card type='inner' title='Patient Information'>
                <Descriptions column={3}>
                  <Descriptions.Item label='Patient Name'>
                    <Space>
                      <FontAwesomeIcon icon={faUser} />
                      {order.patientRecord.firstName} {order.patientRecord.lastName}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label='Patient ID'>{order.patientRecord.idNo}</Descriptions.Item>
                  <Descriptions.Item label='Medical Record #'>{new Date(order.createdAt).getTime()}</Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col span={24}>
              <Card type='inner' title='Order Information'>
                <Descriptions column={2}>
                  <Descriptions.Item label='Order Date'>
                    <Space>
                      <FontAwesomeIcon icon={faCalendar} />
                      {moment(order.dateOrdered).format('YYYY-MM-DD HH:mm')}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label='Ordered By'>
                    {order.medicalProfRecord.firstName} {order.medicalProfRecord.lastName}
                  </Descriptions.Item>
                  <Descriptions.Item label='Priority'>
                    <Tag
                      color={
                        order.orderData.priority === PRIORITY_LEVEL_VALUES.STAT
                          ? 'red'
                          : order.orderData.priority === PRIORITY_LEVEL_VALUES.ROUTINE
                          ? 'orange'
                          : 'blue'
                      }
                    >
                      {getPriorityText(order.orderData.priority)}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label='Specimen Type'>TODO: Confirm</Descriptions.Item>
                  <Descriptions.Item label='Tests Ordered' span={2}>
                    <Space wrap>
                      {order.orderData.orderedTests.map((test, index) => (
                        <Tag key={index} icon={<FontAwesomeIcon style={{ marginRight: 4 }} icon={faFlask} />}>
                          {test}
                        </Tag>
                      ))}
                    </Space>
                  </Descriptions.Item>
                  {order.clinicalNotes && (
                    <Descriptions.Item label='Clinical Notes' span={2}>
                      <Paragraph>
                        <FontAwesomeIcon icon={faNoteSticky} /> {order.clinicalNotes}
                      </Paragraph>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane
          tab={
            <Space>
              <FontAwesomeIcon icon={faFlask} />
              Test Results
              {result?.pathologyReportPDFs?.length > 0 && (
                <Badge count={result.pathologyReportPDFs.length} size='small' />
              )}
            </Space>
          }
          key='results'
          disabled={order.status !== ORDER_STATUS.COMPLETE}
        >
          {renderTestResults()}
        </TabPane>

        <TabPane
          tab={
            <Space>
              <FontAwesomeIcon icon={faHistory} />
              Timeline
            </Space>
          }
          key='timeline'
        >
          <Card type='inner' title='Order Timeline'>
            {renderTimeline()}
          </Card>
        </TabPane>
      </Tabs>

      {/* Cancel Order Modal */}
      <Modal
        title='Cancel Lab Order'
        open={showCancelModal}
        onOk={handleCancelOrder}
        onCancel={() => setShowCancelModal(false)}
        confirmLoading={loading}
        okText='Cancel Order'
        okButtonProps={{ danger: true }}
      >
        <Alert
          message='Are you sure you want to cancel this order?'
          description='This action cannot be undone. Please provide a reason for cancellation.'
          type='warning'
          showIcon
          style={{ marginBottom: 16 }}
        />
        <TextArea
          placeholder='Reason for cancellation'
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          rows={4}
        />
      </Modal>
    </Card>
  )
}

export default OrderDetail
